import React, { useState, useEffect } from 'react'
import { TicketTag } from './core/_models'
import { getTags, createTag, updateTag, deleteTag } from './core/_requests'

interface TagManagerProps {
  show: boolean
  onClose: () => void
  onTagsChanged: () => void
}

const TAG_COLORS = [
  '#3699FF', '#1BC5BD', '#FFA800', '#F64E60', '#8950FC',
  '#181C32', '#E4E6EF', '#50CD89', '#009EF7', '#7239EA',
  '#FFC700', '#F1416C', '#00A3FF', '#47BE7D', '#D9214E'
]

const TagManager: React.FC<TagManagerProps> = ({ show, onClose, onTagsChanged }) => {
  const [tags, setTags] = useState<TicketTag[]>([])
  const [loading, setLoading] = useState(true)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#3699FF')
  const [editingTag, setEditingTag] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')

  useEffect(() => {
    if (show) loadTags()
  }, [show])

  const loadTags = async () => {
    setLoading(true)
    try {
      const res = await getTags()
      setTags(res.data.tags)
    } catch (error) {
      console.error('Failed to load tags:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return
    try {
      await createTag({ name: newTagName.trim(), color: newTagColor })
      setNewTagName('')
      setNewTagColor('#3699FF')
      loadTags()
      onTagsChanged()
    } catch (error) {
      console.error('Failed to create tag:', error)
    }
  }

  const handleUpdateTag = async (tagId: string) => {
    if (!editName.trim()) return
    try {
      await updateTag(tagId, { name: editName.trim(), color: editColor })
      setEditingTag(null)
      loadTags()
      onTagsChanged()
    } catch (error) {
      console.error('Failed to update tag:', error)
    }
  }

  const handleDeleteTag = async (tagId: string) => {
    if (!window.confirm('האם למחוק את התגית? היא תוסר מכל הפניות.')) return
    try {
      await deleteTag(tagId)
      loadTags()
      onTagsChanged()
    } catch (error) {
      console.error('Failed to delete tag:', error)
    }
  }

  const startEditing = (tag: TicketTag) => {
    setEditingTag(tag._id)
    setEditName(tag.name)
    setEditColor(tag.color)
  }

  if (!show) return null

  return (
    <div className="tag-manager-overlay" onClick={onClose}>
      <div className="tag-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>ניהול תגיות</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="modal-body">
          {/* Create new tag */}
          <div className="create-tag-form">
            <div className="color-picker-row">
              {TAG_COLORS.map(color => (
                <button
                  key={color}
                  className={`color-dot ${newTagColor === color ? 'active' : ''}`}
                  style={{ background: color }}
                  onClick={() => setNewTagColor(color)}
                />
              ))}
            </div>
            <div className="create-tag-input">
              <input
                type="text"
                placeholder="שם תגית חדשה..."
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
              />
              <button className="create-btn" onClick={handleCreateTag} disabled={!newTagName.trim()}>
                <i className="bi bi-plus-lg"></i>
                הוסף
              </button>
            </div>
          </div>

          {/* Tags list */}
          <div className="tags-list">
            {loading ? (
              <div className="loading-state"><div className="spinner"></div></div>
            ) : tags.length === 0 ? (
              <div className="empty-tags">אין תגיות עדיין</div>
            ) : (
              tags.map(tag => (
                <div key={tag._id} className="tag-item">
                  {editingTag === tag._id ? (
                    <div className="tag-edit-form">
                      <div className="color-picker-row small">
                        {TAG_COLORS.map(color => (
                          <button
                            key={color}
                            className={`color-dot ${editColor === color ? 'active' : ''}`}
                            style={{ background: color }}
                            onClick={() => setEditColor(color)}
                          />
                        ))}
                      </div>
                      <div className="tag-edit-input">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleUpdateTag(tag._id)}
                          autoFocus
                        />
                        <button className="save-btn" onClick={() => handleUpdateTag(tag._id)}>
                          <i className="bi bi-check-lg"></i>
                        </button>
                        <button className="cancel-btn" onClick={() => setEditingTag(null)}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="tag-chip" style={{ background: `${tag.color}20`, color: tag.color, borderColor: `${tag.color}40` }}>
                        {tag.name}
                      </span>
                      <div className="tag-actions">
                        <button className="edit-btn" onClick={() => startEditing(tag)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="delete-btn" onClick={() => handleDeleteTag(tag._id)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TagManager
